/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { jsx, css } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Breakpoint, useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import messages from './messages';

type Props = {
	appearance?: EditorAppearance;
	isAIFC?: boolean;
	onCancel: () => void;
	onDiscard: () => void;
	text: string;
};

const buttonContainerStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'row',
	justifyContent: 'flex-end',
	alignItems: 'center',
	gap: token('space.100'),
});

const contentContainerStyles = css({
	display: 'flex',
	flex: 1,
	flexDirection: 'row',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: token('space.100'),
});

const contentContainerStylesSmall = css({ alignItems: 'flex-start' });

const contentIconStyles = css({
	marginRight: token('space.025'),
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
	flexShrink: 0,
});

const contentInStreamingIconStyles = css({
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingTop: token('space.050'),
});

const contentInStreamingIconStylesSmall = css({ top: token('space.negative.025') });

const discardFloatingContainerStyle = css({
	display: 'flex',
	flexDirection: 'row',
	gap: token('space.100'),
	/**
	 * Scroll margin is used to ensure that the modal won't be
	 * too close to the top of the editor, which may sometimes
	 * cover the modal slightly.
	 */
	scrollMarginTop: token('space.400'),
});

const discardFloatingContainerStyleFitContent = css({
	justifySelf: 'center',
	width: 'fit-content',
});

const smallDiscardFloatingContainerStyle = css({
	flexDirection: 'column',
	gap: token('space.200'),
});

const styles = cssMap({
	buttonContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		flexDirection: 'row',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.050'),
		// eslint-disable-next-line @atlaskit/design-system/no-unused-css-map, @atlaskit/ui-styling-standard/no-nested-selectors
		button: {
			minHeight: '32px',
			minWidth: '52px',
			borderRadius: token('space.050'),
			alignItems: 'center',
		},
	},
});

const DiscardButtonsContainer = ({
	children,
	isAIFC,
}: {
	children: React.ReactNode;
	isAIFC?: boolean;
}) =>
	isAIFC ? (
		<Inline xcss={styles.buttonContainer}>{children}</Inline>
	) : (
		<div css={buttonContainerStyles}>{children}</div>
	);

export const DiscardScreen = ({
	onCancel,
	onDiscard,
	text,
	appearance,
	isAIFC,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints();

	const setRootContainer = (element: HTMLDivElement | null) => {
		setContainerElement(element);
		setBreakpointsElement(element);
	};

	// Scroll the discard screen into view if it's not fully visible
	useEffect(() => {
		if (containerElement) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates -- Ignored via go/ees017 (to be fixed)
			containerElement.scrollIntoView({ block: 'nearest' });
		}
	}, [containerElement]);

	return (
		<FloatingContainer
			ref={setRootContainer}
			data-testid="discard-screen"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={`discard-screen ${AI_MODAL_SCREEN_CLASS}`}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			css={[
				discardFloatingContainerStyle,
				breakpoint <= Breakpoint.Small && smallDiscardFloatingContainerStyle,
				isAIFC && appearance !== 'chromeless' && discardFloatingContainerStyleFitContent,
			]}
			paddingAdjustments={
				isAIFC
					? appearance === 'chromeless'
						? { right: 0, left: 0 }
						: { right: 4, left: 4 }
					: { top: 1, bottom: 1 }
			}
			rainbowBorder={false}
			appearance={appearance}
			isAIFC={isAIFC}
		>
			<VisuallyHidden>{formatMessage(messages.warningIconLabel)}</VisuallyHidden>
			<div
				css={[
					contentContainerStyles,
					breakpoint <= Breakpoint.Small && !isAIFC && contentContainerStylesSmall,
				]}
			>
				<span
					css={[
						contentIconStyles,
						isAIFC && contentInStreamingIconStyles,
						breakpoint <= Breakpoint.Small && !isAIFC && contentInStreamingIconStylesSmall,
					]}
				>
					{isAIFC ? (
						<WarningIcon
							label={formatMessage(messages.warningIconLabel)}
							color={token('color.icon.warning')}
						/>
					) : (
						<WarningIcon
							label={formatMessage(messages.warningIconLabel)}
							color={token('color.icon.warning')}
						/>
					)}
				</span>
				<Text as="p">{text}</Text>
			</div>
			<DiscardButtonsContainer isAIFC={isAIFC}>
				<Button
					testId="cancel-button"
					spacing="compact"
					onClick={onCancel}
					type="button"
					appearance="subtle"
				>
					{formatMessage(isAIFC ? messages.noLabel : messages.cancelLabel)}
				</Button>
				<Button
					autoFocus
					testId="discard-button"
					spacing="compact"
					onClick={onDiscard}
					type="button"
					appearance="warning"
				>
					{formatMessage(isAIFC ? messages.yesLabel : messages.discardLabel)}
				</Button>
			</DiscardButtonsContainer>
		</FloatingContainer>
	);
};
